import loginService from '../services/login'
import tokenService from '../services/token'
import userService from '../services/user'
import { createSlice } from '@reduxjs/toolkit'

import { setFavourites } from './favouritesReducer'
import { setSuccessNotification, setErrorNotification } from './notificationReducer'

const initialState = null

// toolkit sets up the redux and state
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUser(state, action) {
            return action.payload
        }
    }
})

export const { setUser } = userSlice.actions

const CREDENTIALS_MESSAGE = 'Invalid username or password'
const NETWORK_MESSAGE = "We couldn't reach the server. Please try again."
const GOOGLE_ACCOUNT_MESSAGE = 'This account was created with Google. Use Sign In with Google, or set a password via Forgot password.'

// DRF returns serializer errors as { field: [messages] }, and the password
// validator nests them one level deeper as { password: { password: [...] } }.
const serializerMessages = (body, field) => {
    const value = body?.[field]

    if(!value) return []
    if(Array.isArray(value)) return value.filter(message => typeof message === 'string')
    if(typeof value === 'string') return [value]
    if(typeof value === 'object') return serializerMessages(value, field)

    return []
}

// The profile fetch happens AFTER the token grant, so its failure says nothing
// about the credentials. Retry once, then let the app hydrate on the next load.
const fetchCurrentUser = async () => {
    try {
        return await userService.getCurrent()
    } catch {
        try {
            return await userService.getCurrent()
        } catch {
            return null
        }
    }
}

// Reached only once tokens are granted: the login has SUCCEEDED by this point.
const completeLogin = async (dispatch, tokens, google=false) => {
    tokenService.setToken(tokens.access_token)
    tokenService.setRefreshToken(tokens.refresh_token)
    window.localStorage.setItem('TCAIREMUser', JSON.stringify(tokens))
    if(google) window.localStorage.setItem('TCAIREMGoogle', true)

    const user = await fetchCurrentUser()
    if(user) dispatch(setUser(user))
    dispatch(setSuccessNotification('Logged in successfully'))
}

// Turn a failed token grant into something the user can act on, instead of
// blaming the password for every possible failure.
const loginErrorMessage = async (e, username) => {
    if(!e?.response || e.response.status >= 500) return NETWORK_MESSAGE

    const { status, data } = e.response

    if((status === 400 || status === 401) && data?.error === 'invalid_grant') {
        // The password was rejected — but the account may not have one at all.
        if(await userService.getLoginMethod(username) === 'google') return GOOGLE_ACCOUNT_MESSAGE
    }

    return CREDENTIALS_MESSAGE
}

export const login = (data) => {
    return async dispatch => {
        try {
            const tokens = await loginService.regularLogin(data)
            await completeLogin(dispatch, tokens)
        } catch(e) {
            // A 403 means the request carried an Authorization header the server
            // rejected before it ever looked at the credentials. Drop the stale
            // tokens and give the credentials one clean attempt.
            if(e?.response?.status === 403) {
                tokenService.setToken(null)
                tokenService.setRefreshToken(null)

                try {
                    const tokens = await loginService.regularLogin(data)
                    await completeLogin(dispatch, tokens)
                } catch(retryError) {
                    dispatch(setErrorNotification(await loginErrorMessage(retryError, data?.username)))
                }
                return
            }

            dispatch(setErrorNotification(await loginErrorMessage(e, data?.username)))
        }
    }
}

export const googleLogin = (data) => {
    return async dispatch => {
        try {
            const tokens = await loginService.googleLogin(data)
            await completeLogin(dispatch, tokens, true)
        } catch {
            dispatch(setErrorNotification("Couldn't sign in with Google. If you already have a password account with this email, sign in with your password instead."))
        }
    }
}

export const logout = (expired=false) => {
    return async dispatch => {
        // Clear the local session SYNCHRONOUSLY, before any await: the user is
        // logged out on this device the moment they ask, and nothing can pick up
        // a half-revoked token in the meantime. Revocation still needs the
        // tokens themselves, so capture both (and the Google flag) on the way out.
        const accessToken = tokenService.getAccessToken()
        const refreshToken = tokenService.getRefreshToken()
        const google = Boolean(window.localStorage.getItem('TCAIREMGoogle'))

        tokenService.setToken(null)
        tokenService.setRefreshToken(null)
        window.localStorage.removeItem('TCAIREMUser')
        window.localStorage.removeItem('TCAIREMGoogle')
        // Favourites belong to the account: don't leak them to the next login in this tab.
        dispatch(setFavourites([]))

        try {
            // Revocation reports failure through console.warn, deliberately not
            // through the UI: the user asked to be signed out, and they ARE —
            // the local session is already gone by this point. Whether the
            // server also confirmed it is a developer concern, and nothing the
            // user could act on, so logout reads the same either way.
            if(google) {
                await tokenService.revokeGoogleTokens(accessToken, refreshToken)
            } else {
                await tokenService.revokeTokens(accessToken, refreshToken)
            }
        } finally {
            window.localStorage.setItem('TCAIREMUser', JSON.stringify({ anon: true }))
            dispatch(setUser({ anon: true }))

            if(!expired) dispatch(setSuccessNotification('Logged out successfully'))
        }
    }
}

export const setUserOnRefresh = () => {
    return async dispatch => {
        const readStored = () => {
            try {
                return JSON.parse(window.localStorage.getItem('TCAIREMUser'))
            } catch {
                return null // corrupt storage reads as anonymous
            }
        }
        const refreshGrant = () => window.localStorage.getItem('TCAIREMGoogle')
            ? tokenService.refreshGoogle()
            : tokenService.refresh()
        const data = readStored()

        if(data && !data.anon) {
            tokenService.setToken(data.access_token)
            tokenService.setRefreshToken(data.refresh_token)

            // One /me request when the access token is still good; any failure
            // (e.g. an expired token) falls through to the refresh grant.
            try {
                dispatch(setUser(await userService.getCurrent()))
            } catch {
                try {
                    let tokens = null

                    try {
                        tokens = await refreshGrant()
                    } catch(error) {
                        // Another tab may have rotated the refresh token; retry once with it.
                        const stored = readStored()
                        if(!stored?.refresh_token || stored.refresh_token === data.refresh_token) throw error
                        tokenService.setRefreshToken(stored.refresh_token)
                        tokens = await refreshGrant()
                    }
                    tokenService.setToken(tokens.access_token)
                    tokenService.setRefreshToken(tokens.refresh_token)
                    window.localStorage.setItem('TCAIREMUser', JSON.stringify(tokens))
                    const user = await userService.getCurrent()
                    dispatch(setUser(user))
                } catch(error) {
                    const status = error.response?.status
                    if(status === 400 || status === 401) {
                        dispatch(setErrorNotification('Your session expired. Please sign in again.'))
                        dispatch(logout(true))
                    } else {
                        // Transient (offline, 5xx): anonymous in memory only, keep the stored session.
                        dispatch(setUser({ anon: true }))
                    }
                }
            }
        } else {
            window.localStorage.setItem('TCAIREMUser', JSON.stringify({ anon: true }))
            dispatch(setUser({ anon: true }))
        }
    }
}

export const createUser = (data) => {
    return async dispatch => {
        const reportFailure = (body) => {
            const passwordMessages = serializerMessages(body, 'password')
            if(passwordMessages.length) {
                passwordMessages.forEach(message => {
                    dispatch(setErrorNotification(message))
                })
                return
            }

            const emailMessages = serializerMessages(body, 'email')
            if(emailMessages.length) {
                // A duplicate email is the one case worth naming; anything else
                // (malformed address, …) keeps the serializer's own wording.
                const duplicate = emailMessages.some(message => /already exists|must be unique/i.test(message))
                dispatch(setErrorNotification(duplicate ? 'An account with this email already exists.' : emailMessages[0]))
                return
            }

            dispatch(setErrorNotification('Error creating account, please try again later!'))
        }

        try {
            await userService.create(data)
            dispatch(setSuccessNotification('Account created — check your email to verify your account.'))
        } catch(e) {
            if(e?.response?.status !== 400) {
                // Anything else — another 4xx, a 5xx, or no response at all (a
                // genuine network failure) — stays generic, matching the other
                // converted thunks in this module.
                dispatch(setErrorNotification('Error creating account, please try again later!'))
                return
            }

            reportFailure(e.response.data)
        }
    }
}

export const updateUser = (data) => {
    return async dispatch => {
        try {
            const user = await userService.update(data.id, data)
            dispatch(setUser(user))
            dispatch(setSuccessNotification('Profile updated'))
        } catch {
            dispatch(setErrorNotification('Error updating profile'))
        }
    }
}

export const changeUserPassword = (data) => {
    return async dispatch => {
        // Only the set-password path omits old_password — and only that path
        // can hold a stale has_usable_password flag or flip it on the server.
        const oldPasswordSent = Boolean(data) && 'old_password' in data

        try {
            await userService.changePassword(data)

            // The change is already committed — say so before anything else.
            dispatch(setSuccessNotification('Password updated successfully'))

            // Setting a first password flips has_usable_password, which the
            // Profile form branches on. Without this refresh the form keeps
            // offering "Set Password" and the next submit is rejected for a
            // missing old password. An ordinary change cannot flip the flag.
            if(!oldPasswordSent) {
                const refreshed = await fetchCurrentUser()
                if(refreshed) dispatch(setUser(refreshed))
            }
        } catch(e) {
            const status = e?.response?.status

            if(status === 429) {
                dispatch(setErrorNotification('Too many requests. Please try again later.'))
                return
            }

            if(status !== 400) {
                // Anything else — another 4xx, a 5xx, or no response at all (a
                // genuine network failure) — stays generic.
                dispatch(setErrorNotification('Error updating password, please try again later'))
                return
            }

            const body = e.response.data ?? {}
            // The change-password serializer files validator rejections as
            // { new_password: { password: [...] } }. Hand serializerMessages
            // the INNER object (unlike createUser, which passes the whole
            // body): serializerMessages(body, 'new_password') would find
            // nothing — its recursion reuses the same key, so it can't walk
            // new_password → password.
            const passwordMessages = serializerMessages(body.new_password ?? body.password, 'password')

            // Validator messages win over old_password when both are present.
            if(passwordMessages.length) {
                passwordMessages.forEach(message => {
                    dispatch(setErrorNotification(message))
                })
            } else if (body.old_password && !oldPasswordSent) {
                // The stored user said "no password yet" but the backend
                // disagrees: the flag was stale (failed post-set refresh,
                // or a second tab). Explain, then re-fetch so the form
                // flips to "Change Password" instead of dead-ending.
                dispatch(setErrorNotification('A password is already set on this account — enter your current password to change it.'))
                const refreshed = await fetchCurrentUser()
                if(refreshed) dispatch(setUser(refreshed))
            } else if (body.old_password) {
                dispatch(setErrorNotification('Old password is not correct, please try again'))
            } else {
                dispatch(setErrorNotification('Error updating password, please try again later'))
            }
        }
    }
}

export const changeEmail = (data) => {
    return async dispatch => {
        // Only a passwordless account's form omits password — and only that
        // path can hold a stale has_usable_password flag (see changeUserPassword).
        const passwordSent = Boolean(data) && 'password' in data

        try {
            await userService.changeEmail(data)
            // the address is only staged at this point — nothing changes until the
            // new address confirms it (see confirmEmailChange)
            dispatch(setSuccessNotification('Check your new email for a confirmation link'))
        } catch(e) {
            const status = e?.response?.status

            if(status === 429) {
                dispatch(setErrorNotification('Too many requests. Please try again later.'))
                return
            }

            if(status !== 400) {
                // Anything else — another 4xx, a 5xx, or no response at all (a
                // genuine network failure) — stays generic.
                dispatch(setErrorNotification('Error updating email, please try again later'))
                return
            }

            const body = e.response.data ?? {}

            if (body.password && !passwordSent) {
                // The stored user said "no password yet" but the backend
                // disagrees: the flag was stale (failed refresh, or a second
                // tab). A wrong-password complaint would point at a field the
                // form doesn't render — explain, then re-fetch so it appears.
                dispatch(setErrorNotification('A password is set on this account — enter it to change your email.'))
                const refreshed = await fetchCurrentUser()
                if(refreshed) dispatch(setUser(refreshed))
            } else if (body.password) {
                dispatch(setErrorNotification('Password is not correct, please try again'))
            } else if(body.email) {
                dispatch(setErrorNotification('The email you specified is already in use!'))
            } else {
                dispatch(setErrorNotification('Error updating email, please try again later'))
            }
        }
    }
}

export const resetPassword = (data) => {
    return async dispatch => {
        try {
            await userService.passwordReset(data)
        } catch(e) {
            // The endpoint is rate limited per client IP (issue #339). A 429 means
            // nothing was mailed, so saying "check your inbox" would leave the user
            // waiting on an email that was never queued. This branch is safe for
            // neutrality: the throttle fires in DRF's initial() before the view body
            // runs, so it is decided by the caller's IP alone and says nothing about
            // whether the address is registered.
            if(e?.response?.status === 429) {
                dispatch(setErrorNotification('Too many password reset requests. Please try again later.'))
                return false
            }
            // With DJANGO_REST_PASSWORDRESET_NO_INFORMATION_LEAKAGE on, the address is
            // never looked up before the response, so the only 400 left is the
            // serializer's EmailField rejecting the string itself. That verdict is a
            // pure function of what the caller typed and cannot enumerate anything —
            // and it is reachable, because this page's regex accepts addresses
            // Django's EmailValidator does not (a@b..com, .a@b.com). Claiming "we've
            // sent instructions" there leaves the user waiting on mail that was never
            // queued.
            if(e?.response?.status === 400) {
                dispatch(setErrorNotification("That email address doesn't look valid. Please check it and try again."))
                return false
            }
            // Everything else — 5xx, network failure — stays swallowed, and that is
            // the deliberate trade: a 500 CAN be account-dependent (see issue #470),
            // so reporting it would rebuild the oracle out of error messages. The
            // cost is a false success on a genuine outage.
        }

        dispatch(setSuccessNotification("If an account exists for that email, we've sent password reset instructions."))
        return true
    }
}

export const resetPasswordConfirm = (data) => {
    return async dispatch => {
        try {
            await userService.passwordResetConfirm(data)
            dispatch(setSuccessNotification('Password reset successfully'))
            return true
        } catch(e) {
            // passwordResetConfirm throws on every non-2xx, so the branching belongs
            // here: a rejected new password answers 400 { password: [...] }, while an
            // unknown OR expired token answers 404 (the library raises Http404 for
            // both). This endpoint is rate limited per client IP too (issue #339); the
            // throttle fires in DRF's initial(), before the token is ever looked at,
            // so a 429 says nothing about the link — reporting it as the generic
            // failure (or worse, as an expired token) would send a user with a
            // perfectly good link off to request another one. Anything else — 5xx,
            // no response at all — is generic.
            if(e?.response?.status === 400) {
                const passwordMessages = serializerMessages(e.response.data, 'password')
                if(passwordMessages.length) {
                    passwordMessages.forEach(message => {
                        dispatch(setErrorNotification(message))
                    })
                    return false
                }
            } else if(e?.response?.status === 404) {
                dispatch(setErrorNotification('This reset link is invalid or has expired. Request a new one.'))
                return false
            } else if(e?.response?.status === 429) {
                dispatch(setErrorNotification('Too many attempts. Please try again later.'))
                return false
            }

            dispatch(setErrorNotification('Error resetting password, please try again later'))
            return false
        }
    }
}

export const deleteUser = (id, password) => {
    return async dispatch => {
        try {
            await userService.destroy(id, password)
            dispatch(setSuccessNotification('Your account was deleted!'))
            dispatch(logout())
            window.location.reload()
        } catch (e) {
            // A wrong password is the one failure the user can fix, so name it; the
            // backend answers 400 {"password": ["Wrong password."]} (accounts/views.py
            // UserViewUpdateDeleteAPIView.destroy). Everything else stays generic.
            if (e?.response?.status === 400 && e.response.data?.password) {
                dispatch(setErrorNotification('Incorrect password. Your account was not deleted.'))
            } else {
                dispatch(setErrorNotification('There was an error deleting your account. Please try again.'))
            }
        }
    }
}

// Ends every session of the account on the server, this one included, then clears
// the local one. Resolves true once signed out so callers can leave the page.
export const signOutEverywhere = () => {
    return async dispatch => {
        try {
            await userService.signOutEverywhere()
        } catch {
            dispatch(setErrorNotification('There was an error signing you out of all devices. Please try again.'))
            return false
        }
        await dispatch(logout())
        return true
    }
}

export const activateUser = (id, token) => {
    return async dispatch => {
        try {
            await userService.activateUser(id, token)
            dispatch(setSuccessNotification('Account activated!'))
        } catch(e) {
            // The backend's activate view is a plain Django view with a single
            // failure path: a bad/malformed user id and an invalid/expired token
            // both fall through to the same flat 400 (no separate 404).
            if(e?.response?.status === 400) {
                dispatch(setErrorNotification('This activation link is invalid or has expired. Request a new one.'))
                return
            }

            dispatch(setErrorNotification('Error activating account, please try again later'))
        }
    }
}

export const confirmEmailChange = (id, token) => {
    return async dispatch => {
        try {
            await userService.confirmEmailChange(id, token)

            dispatch(setSuccessNotification('Email confirmed!'))
            // the signed-in profile still carries the old address
            const user = await fetchCurrentUser()
            if(user) dispatch(setUser(user))
        } catch(e) {
            if(e?.response?.status !== 400) {
                dispatch(setErrorNotification('Error confirming email, please try again later'))
                return
            }

            dispatch(setErrorNotification('This confirmation link is invalid or has expired. Request the change again.'))
        }
    }
}

export default userSlice.reducer